import {
  Component,
  HostListener,
  OnInit,
  OnDestroy,
  inject,
  runInInjectionContext,
  effect,
  Injector,
  AfterViewInit,
  NgZone,
  ViewChild,
} from '@angular/core';
import {
  NavigationStart,
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { PAGE } from './routes';
import {
  AlertController,
  IonApp,
  IonButton,
  IonCol,
  IonContent,
  IonGrid,
  IonItem,
  IonList,
  IonMenu,
  IonMenuToggle,
  IonModal,
  IonRouterOutlet,
  IonRow,
  IonSplitPane,
  IonToolbar,
  Platform,
} from '@ionic/angular/standalone';
import { environment } from 'src/environments/environment';
import { initializeApp, FirebaseOptions } from 'firebase/app';
import { Capacitor } from '@capacitor/core';
import { EventName } from './core/services/analytics/event-names';
import { AuthService } from './core/services/auth/auth.service';
import { ProfileService } from './core/services/profile/profile.service';
import { AnalyticsService } from './core/services/analytics/analytics.service';
import { StorageService } from './core/services/storage/storage.service';
import { PushMessagingService } from './core/services/messaging/push-messaging.service';
import { LocalMessagingService } from './core/services/messaging/local-messaging.service';
import { PopupCampaignService } from './core/services/popup-campaign/popup-campaign.service';
import { DataLayerService } from './core/services/datalayer/datalayer.service';
import { UserStateService } from './core/services/user-state/user-state.service';
import { Theme, ThemeService } from './core/services/theme/theme.service';
import { InitService } from './core/services/init/init.service';
import { BootupService } from './core/services/bootup/bootup.service';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { CustomisationService } from './core/services/customisation/customisation.service';
import { LocationService } from './core/services/location/location.service';
import { TranslocoService } from '@jsverse/transloco';
import { GameId } from './model/game/game-id';
import { JobService } from './core/services/job/job.service';
import { Job } from './core/services/job/job';
import { SingularService } from './core/services/singular/singular.service';
import { MobileServices } from 'mobile-services';
import { IconComponent } from './ui/icon/icon.component';
import { ButtonComponent } from './ui/button/button.component';
import { TranslocoDirective } from '@jsverse/transloco';
import { NgClass } from '@angular/common';

const HIDE_DOWNLOAD_BANNER_KEY = 'hide_download_banner_key';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    IonApp,
    IonButton,
    IonCol,
    IonContent,
    IonGrid,
    IonItem,
    IonList,
    IonMenu,
    IonMenuToggle,
    IonRouterOutlet,
    IonRow,
    IonSplitPane,
    IonToolbar,
    RouterLink,
    RouterLinkActive,
    TranslocoDirective,
    NgClass,
    IconComponent,
    ButtonComponent,
  ],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(IonModal) modal!: IonModal;

  private auth = inject(AuthService);
  private profileService = inject(ProfileService);
  private router = inject(Router);
  private platform = inject(Platform);
  private analyticsService = inject(AnalyticsService);
  private storageService = inject(StorageService);
  private pushMessagingService = inject(PushMessagingService);
  private localMessagingService = inject(LocalMessagingService);
  private popupCampaignService = inject(PopupCampaignService);
  private dataLayerService = inject(DataLayerService);
  private injector = inject(Injector);
  private initService = inject(InitService);
  private bootupService = inject(BootupService);
  private ngZone = inject(NgZone);
  private locationService = inject(LocationService);
  private singularService = inject(SingularService);
  customisationService = inject(CustomisationService);
  userStateService = inject(UserStateService);
  private alertController = inject(AlertController);
  private translocoService = inject(TranslocoService);
  private jobService = inject(JobService);

  private watcherSubscription: Subscription | undefined;

  Theme = Theme;
  GameId = GameId;

  themeService = inject(ThemeService);
  mobileMode = false;
  menuOpen = false;
  showSidebar = false;
  PAGE = PAGE;
  isMobileWeb = false;
  isNativePlatform = false;
  canHideDownloadApp = true;
  unreadCount: string | undefined;
  currentUrl: string | undefined;
  previousUrl: string | undefined;

  constructor() {
    this.applySafeAreaInsets();
  }

  private async initialise() {
    this.analyticsService.log(EventName.AppConstructor);

    await this.initializeFirebase();
    await this.analyticsService.initialise();

    const job = this.jobService.create(async () => {
      await this.bootupService.initialise();
    });
    job.showNoInternetRetry(Job.InfiniteRetry);
    job.addErrorHandler().showFatalError().reloadWindow();
    await job.runNoThrow();

    this.analyticsService.log(EventName.FirebaseInitialised);

    this.updateMobileMode();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(async (event) => {
        const navStartEvent = event as NavigationStart;
        if (navStartEvent.navigationTrigger === 'popstate' && this.previousUrl) {
          this.popupCampaignService.navButtonClicked(this.previousUrl);
        }
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(async (event) => {
        const url = (event as NavigationEnd).url;
        this.currentUrl = url;
        this.previousUrl = url;
        const isLoggedIn = this.initService.initialised();
        this.canHideDownloadApp = await this.canHideDownloadBanner();
        this.showSidebar = AppComponent.canShowSidebar(url, isLoggedIn ?? false);
      });

    runInInjectionContext(this.injector, () => {
      effect(async () => {
        const initialised = this.initService.initialised();
        const locationAllowed = this.locationService.locationAllowed();

        if (initialised && locationAllowed) {
          await this.pushMessagingService.requestPermissions();
          await this.localMessagingService.requestPermissions();
        }
      });
    });
  }

  async ngOnInit(): Promise<void> {
    this.analyticsService.log(EventName.AppOnInit);
    await this.initialise();

    this.isMobileWeb = this.platform.is('mobile');
    this.isNativePlatform = Capacitor.isNativePlatform();
    this.dataLayerService.setPlatform(this.platform);

    if (this.isNativePlatform) {
      App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        this.ngZone.run(async () => {
          this.analyticsService.log(EventName.AppUrlOpen);

          const decodedUrl = decodeURIComponent(event.url);
          const params = new URL(decodedUrl).searchParams;
          const continueUrl = params.get('continueUrl');

          const hasEmailLinkUrl = await this.auth.isSignInWithEmailLink(decodedUrl).runNoThrow();
          if (hasEmailLinkUrl && continueUrl?.includes(PAGE.SIGNIN)) {
            this.analyticsService.log(EventName.NativeRedirectUrlLink);

            // Go back to the sign-in page, set the email link on auth service
            this.auth.setLastEmailLink(decodedUrl);

            // Navigate back, set some query parameters so it refreshes the page
            this.router.navigate([
              `${PAGE.SIGNIN}`,
              {
                emailLink: true,
              },
            ]);
          }
        });
      });
    }

    try {
      await CapacitorUpdater.notifyAppReady();
    } catch (error) {
      this.analyticsService.handleError(error, 'Capacitor update notify ready');
    }
  }

  ngOnDestroy(): void {
    App.removeAllListeners();
    this.watcherSubscription?.unsubscribe();
  }

  ngAfterViewInit() {
    this.analyticsService.log(EventName.AppView);
  }

  logout() {
    this.auth
      .signOut()
      .then(() => {
        this.router.navigate([PAGE.SIGNIN]);
      })
      .catch((error) => {
        this.analyticsService.handleError(error, 'App logout sign out');
      });
  }

  menuOpened() {
    this.menuOpen = true;
    this.analyticsService.log(EventName.SideMenuOpen);
  }

  menuClosed() {
    this.menuOpen = false;
    this.analyticsService.log(EventName.SideMenuClose);
  }

  showChat(): boolean {
    return !this.mobileMode && this.userStateService.onboarded;
  }

  playClick(): void {
    this.router.navigate([PAGE.CHOOSE_GAME]);
  }

  async sideMenuInviteClick(): Promise<void> {
    if (environment.telegram) {
      const link = await this.profileService.getTelegramLink();
      window.Telegram.WebApp.openTelegramLink(link);
    }
  }

  clinchesClick(): void {
    this.analyticsService.log(EventName.SideMenuClinchesClick);
  }

  async chatClick(): Promise<void> {
    this.analyticsService.log(EventName.SideMenuChatClick);
    const alert = await this.alertController.create({
      header: this.translocoService.translate('app-component.join-chat-title'),
      message: this.translocoService.translate('app-component.join-chat-message'),
      cssClass: 'alert',
      buttons: [
        {
          text: this.translocoService.translate('app-component.join-chat-cancel'),
          role: 'cancel',
          cssClass: 'alert-button',
        },
        {
          text: this.translocoService.translate('app-component.join-chat-confirm'),
          cssClass: 'alert-button',
          handler: () => {
            alert.dismiss();
            window.open('https://t.me/clinchgg', '_blank');
          },
        },
      ],
    });
    alert.present();
  }

  profileClick(): void {
    this.analyticsService.log(EventName.SideMenuProfileClick);
  }

  downloadAppClick(): void {
    this.analyticsService.log(EventName.SideMenuDownloadAppClick);
    if (environment.telegram) {
      window.Telegram.WebApp.openLink(
        'https://clinch.gg/?wpsrc=Telegram&wpcn=telegram_link&utm_campaign=telegram_link&utm_source=Telegram&utm_medium=socials',
        { try_instant_view: false },
      );
    } else {
      this.singularService.openApp();
    }
  }

  logoutClick(): void {
    this.analyticsService.log(EventName.SideMenuLogoutClick);
  }

  privacyClick(): void {
    this.analyticsService.log(EventName.SideMenuPrivacyClick);
  }

  termsClick(): void {
    this.analyticsService.log(EventName.SideMenuTermsClick);
  }

  responsibleGamingClick(): void {
    this.analyticsService.log(EventName.SideMenuResponsibleGamingClick);
  }

  faqClick(): void {
    this.analyticsService.log(EventName.SideMenuFAQClick);
  }

  depositlessClick(): void {
    this.analyticsService.log(EventName.SideMenuDepositlessClick);
  }

  openAppDownloadClick(): void {
    this.analyticsService.log(EventName.OpenAppDownloadClick);

    if (environment.telegram) {
      window.Telegram.WebApp.openLink(
        'https://clinch.gg/?wpsrc=Telegram&wpcn=telegram_link&utm_campaign=telegram_link&utm_source=Telegram&utm_medium=socials',
        { try_instant_view: false },
      );
    } else {
      this.singularService.openApp();
    }

    this.hideDownloadBanner();
  }

  closeDownloadBannerClick(): void {
    this.analyticsService.log(EventName.CloseDownloadBannerClick);
    this.hideDownloadBanner();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateMobileMode();
  }

  @HostListener('window:mouseout', ['$event'])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onMouseOut(event: any) {
    // Check if the mouse is leaving the document area
    if (!event.relatedTarget && !event.toElement) {
      // Mouse is leaving the document area, trigger page view event
      this.popupCampaignService.leaveSiteDetected(this.router.url);
    }
  }

  isTelegram(): boolean {
    return environment.telegram;
  }

  isDownloadBannerVisible() {
    // TODO: Disable top banner until we can redesign it
    return false;

    return (
      this.customisationService.isDownloadAppEnabled() &&
      !this.canHideDownloadApp &&
      !this.currentUrl?.startsWith(`/${PAGE.OPEN_WALLET}`) &&
      !this.currentUrl?.startsWith(`/${PAGE.SIGNIN}`)
    );
  }

  onThemeClicked() {
    this.modal.present();
  }

  onThemeCloseClick() {
    this.modal.dismiss();
  }

  async canDismiss(data?: unknown, role?: string) {
    return role !== 'gesture';
  }

  private updateMobileMode() {
    this.mobileMode = window.innerWidth < 992;
  }

  isMobile(): boolean {
    return this.platform.is('mobile');
  }

  private async canHideDownloadBanner(): Promise<boolean> {
    const canHide = await this.storageService.getItem<string>(HIDE_DOWNLOAD_BANNER_KEY);
    return canHide === 'hide';
  }

  private hideDownloadBanner() {
    this.canHideDownloadApp = true;
    this.storageService.setItem<string>(HIDE_DOWNLOAD_BANNER_KEY, 'hide').catch((error) => {
      this.analyticsService.handleError(error, 'Hide download banner');
    });
  }

  private async initializeFirebase(): Promise<void> {
    if (this.platform.is('capacitor')) {
      return;
    }
    initializeApp(environment.firebase as FirebaseOptions);
  }

  private static canShowSidebar(url: string, isLoggedIn: boolean): boolean {
    return !(
      url === `/${PAGE.GEO_LOCATION_CHECK}` ||
      url.startsWith(`/${PAGE.OPEN_WALLET}`) ||
      url.startsWith(`/${PAGE.SIGNIN}`) ||
      url.startsWith(`/?`) ||
      url === '/' ||
      url.startsWith(`/?redirect=true`) ||
      url.startsWith(`/${PAGE.GAME_VIEWER}/`) ||
      url === `/${PAGE.UPDATE_REQUIRED}` ||
      url === `/${PAGE.UNAVAILABLE}` ||
      url === `/${PAGE.DEMO_SIGNIN}` ||
      (url === `/${PAGE.PRIVACY}` && !isLoggedIn) ||
      (url === `/${PAGE.TERMS}` && !isLoggedIn)
    );
  }

  private applySafeAreaInsets() {
    this.platform.ready().then(async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          const insets = await MobileServices.getSafeAreaInsets();

          const root = document.documentElement;

          root.style.setProperty('--native-safe-area-inset-top', `${insets.top}px`);
          root.style.setProperty('--native-safe-area-inset-bottom', `${insets.bottom}px`);
          root.style.setProperty('--native-safe-area-inset-left', `${insets.left}px`);
          root.style.setProperty('--native-safe-area-inset-right', `${insets.right}px`);

          root.style.setProperty('--ion-safe-area-top', `${insets.top}px`);
          root.style.setProperty('--ion-safe-area-bottom', `${insets.bottom}px`);
          root.style.setProperty('--ion-safe-area-left', `${insets.left}px`);
          root.style.setProperty('--ion-safe-area-right', `${insets.right}px`);
        } catch (error) {
          this.analyticsService.handleError(error, 'Set safe area insets');
        }
      }
    });
  }
}
